// NIGHT-DIRECTSTAFF-2026-10-17 §5: pull-to-refresh on the lists (phones). The installed app runs full-screen, where
// Chrome's own pull-to-refresh is gone, so the lists bring their own: pull down from the top, let go, they reload
// (the same quiet reload the 45-second live refresh does). Touch only; a mouse never sees it.
import { useEffect, useRef, useState } from 'react';
import { pullState } from './lib/appPolish';

/** scrollEl(): the element the list scrolls in (the CRM's .crm-content), checked with the window -- both must be at the top. */
export default function PullToRefresh({ onRefresh, enabled = true, scrollEl = () => null }) {
  const [pull, setPull] = useState({ dist: 0, armed: false });
  const [busy, setBusy] = useState(false);
  const start = useRef(null);
  const live = useRef({ onRefresh, scrollEl, busy });
  live.current = { onRefresh, scrollEl, busy };
  const pullRef = useRef(pull);
  pullRef.current = pull;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const atTop = () => { const el = live.current.scrollEl(); return (window.scrollY || 0) <= 0 && (!el || el.scrollTop <= 0); };
    const onStart = (e) => { start.current = !live.current.busy && e.touches.length === 1 && atTop() ? e.touches[0].clientY : null; };
    const onMove = (e) => { if (start.current != null) setPull(pullState(start.current, e.touches[0].clientY, atTop())); };
    const onEnd = async () => {
      const armed = start.current != null && pullRef.current.armed;
      start.current = null;
      setPull({ dist: 0, armed: false });
      if (!armed) return;
      setBusy(true);
      try { await live.current.onRefresh(); } finally { setBusy(false); }
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled]);

  if (!busy && pull.dist <= 0) return null;
  return (
    <div data-testid="pull-refresh" role="status" aria-live="polite"
      style={{ position: 'fixed', top: 8 + (busy ? 40 : pull.dist), left: '50%', transform: 'translateX(-50%)', zIndex: 1500,
        background: '#0f172a', color: 'white', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 600, pointerEvents: 'none' }}>
      {busy ? 'Refreshing…' : pull.armed ? 'Let go to refresh' : 'Pull to refresh'}
    </div>
  );
}
